import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Button, FlagIcon, StarIcon, CountUp } from './UI';
import { ShieldCheck, Layers, Hexagon, Feather, Eye, ChevronDown, Award } from 'lucide-react';
export const Hero = () => {
  const tags = [{
    icon: Feather,
    label: '1.4 LBS LIGHT'
  }, {
    icon: Eye,
    label: 'ZERO-FOG OPTICS'
  }];
  return <section className="relative min-h-screen pt-[106px] bg-base-black overflow-hidden flex items-center" data-id="element-1043">
      {/* Cinematic Background Image - now full presence */}
      <div className="absolute inset-0 z-0" data-id="element-1044">
        <motion.img initial={{
        scale: 1.08
      }} animate={{
        scale: 1
      }} transition={{
        duration: 2.5,
        ease: 'easeOut'
      }} src="https://cdn.magicpatterns.com/uploads/nm5pJh4HTs89bDJmjH5guT/hf_20260505_164053_a2220f21-6ee4-4e03-a216-305128115bb2.png" alt="BLACKOUT UNIT Tactical Mask in Combat Field" className="w-full h-full object-cover object-center" data-id="element-1045" />
        {/* Strong left-side darkening for text legibility, image fully visible on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-base-black via-base-black/80 via-40% to-transparent" data-id="element-1046"></div>
        {/* Subtle bottom fade to blend into next section */}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-base-black to-transparent" data-id="element-1047"></div>
        {/* Mobile: extra darkening so text remains readable */}
        <div className="absolute inset-0 bg-base-black/40 lg:bg-transparent" data-id="element-1048"></div>
      </div>

      {/* Scanline */}
      <div className="scanline-overlay animate-scanline" data-id="element-1049"></div>

      {/* Vertical accent line */}
      <div className="absolute left-0 top-1/4 bottom-1/4 w-[2px] bg-gradient-to-b from-transparent via-acid-green to-transparent opacity-30 z-10" data-id="element-1050"></div>

      {/* HUD Corner Brackets - full hero */}
      <div className="absolute top-[120px] left-6 w-12 h-12 border-t-2 border-l-2 border-acid-green opacity-40 z-10" data-id="element-1051"></div>
      <div className="absolute top-[120px] right-6 w-12 h-12 border-t-2 border-r-2 border-acid-green opacity-40 z-10" data-id="element-1052"></div>
      <div className="absolute bottom-6 left-6 w-12 h-12 border-b-2 border-l-2 border-acid-green opacity-40 z-10" data-id="element-1053"></div>
      <div className="absolute bottom-6 right-6 w-12 h-12 border-b-2 border-r-2 border-acid-green opacity-40 z-10" data-id="element-1054"></div>

      {/* Floating tactical data overlays */}
      <motion.div initial={{
      opacity: 0,
      x: 30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 0.8,
      duration: 0.8
    }} className="hidden lg:block absolute top-32 right-12 bg-base-black/70 backdrop-blur-md border border-acid-green/30 px-4 py-3 z-20" data-id="element-1055">
        <div className="flex items-center gap-2 mb-1" data-id="element-1056">
          <span className="w-1.5 h-1.5 bg-acid-green rounded-full animate-pulse" data-id="element-1057"></span>
          <span className="font-barlow text-[9px] text-acid-green tracking-[3px]" data-id="element-1058">
            SECTOR 7 / FIELD ACTIVE
          </span>
        </div>
        <div className="font-bebas text-base text-white tracking-wider" data-id="element-1059">
          UNIT 04 — DEPLOYED
        </div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      x: -30
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: 1,
      duration: 0.8
    }} className="hidden lg:block absolute bottom-32 right-16 bg-base-black/70 backdrop-blur-md border border-acid-green/30 px-4 py-3 z-20 max-w-[220px]" data-id="element-1060">
        <div className="font-barlow text-[9px] text-acid-green tracking-[3px] mb-1" data-id="element-1061">
          SHELL // GFR COMPOSITE
        </div>
        <div className="font-bebas text-lg text-white tracking-wide leading-none mb-1" data-id="element-1062">
          300+ LBS RATED
        </div>
        <div className="font-inter text-[10px] text-text-dimmed" data-id="element-1063">
          Crush-tested. Field-proven. Battle-born.
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1064">
        <div className="max-w-3xl" data-id="element-1065">
          {/* Brand Authority Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-3 border-l-2 border-acid-green pl-4 mb-8" data-id="element-1066">
            <div data-id="element-1067">
              <div className="font-barlow font-bold text-[10px] text-acid-green tracking-[5px] uppercase mb-1" data-id="element-1068">
                THE OFFICIAL BRAND
              </div>
              <div className="font-bebas text-xl md:text-2xl text-white tracking-[3px] leading-none" data-id="element-1069">
                EST. 2019 — AMERICAN BUILT
              </div>
            </div>
          </motion.div>

          {/* Eyebrow */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="inline-flex items-center gap-3 border border-acid-green/30 bg-acid-green/5 backdrop-blur-sm px-3 py-1.5 mb-6" data-id="element-1070">
            <span className="w-1.5 h-1.5 bg-acid-green animate-pulse" data-id="element-1071"></span>
            <span className="font-barlow font-bold text-[10px] text-acid-green tracking-[4px] uppercase" data-id="element-1072">
              CLASSIFIED EDITION — UNITS DEPLOYING NOW
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="font-bebas uppercase mb-6" data-id="element-1073">
            <span className="block text-text-body text-xl md:text-2xl tracking-[6px] mb-3" data-id="element-1074">
              WE LOOKED. WE WAITED.
            </span>
            <span className="block text-white text-5xl md:text-7xl lg:text-[100px] leading-[0.9] tracking-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]" data-id="element-1075">
              THE INTERNET'S
            </span>
            <span className="block text-acid-green text-5xl md:text-7xl lg:text-[100px] leading-[0.9] tracking-tight drop-shadow-[0_0_30px_rgba(200,255,0,0.3)]" data-id="element-1076">
              MOST WANTED MASK
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.5
        }} className="border-l-2 border-acid-green pl-5 mb-10 max-w-2xl" data-id="element-1077">
            <p className="font-barlow text-lg md:text-xl text-white/90 leading-[1.7]" data-id="element-1078">
              Built for airsoft players, streamers, bikers, creators and
              tactical enthusiasts.{' '}
              <span className="text-acid-green font-bold uppercase" data-id="element-1079">
                The mask that looks illegal to own.
              </span>
              <br data-id="element-1080" />
              <br data-id="element-1081" />
              <span className="text-text-body" data-id="element-1082">
                Aggressive design. Built for performance. Shipped to 50 states
                and 40 countries.
              </span>
            </p>
          </motion.div>

          {/* Category Tags with custom icons - mobile optimized */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-x-5 sm:gap-y-3 font-barlow font-bold text-[11px] sm:text-xs text-white/80 tracking-[2px] uppercase mb-10" data-id="element-1083">
            {tags.map((tag, i) => <div key={i} className="flex items-center gap-2 border border-acid-green/15 sm:border-0 bg-base-black/40 sm:bg-transparent backdrop-blur-sm sm:backdrop-blur-none px-3 py-2 sm:p-0" data-id="element-1084">
                <tag.icon size={14} className="text-acid-green shrink-0" strokeWidth={2} data-id="element-1085" />
                <span className="leading-tight" data-id="element-1086">{tag.label}</span>
                {i < tags.length - 1 && <span className="hidden sm:inline text-acid-green/30 ml-3" data-id="element-1087">
                    /
                  </span>}
              </div>)}
          </motion.div>

          {/* CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9
        }} className="flex flex-col sm:flex-row gap-4 mb-16" data-id="element-1088">
            <Button variant="primary" className="sm:w-auto px-10 py-5 text-base" onClick={() => {
            document.getElementById('the-mask')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} data-id="element-1089">
              GEAR UP — DEPLOY THE MASK
            </Button>
            <Button variant="secondary" className="sm:w-auto px-10 py-5 text-base" onClick={() => {
            document.getElementById('the-mask')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} data-id="element-1090">
              <ChevronDown size={16} className="mr-2 animate-bounce" data-id="element-1091" />
              SEE HOW WE BUILT IT
            </Button>
          </motion.div>

          {/* Stats Row - Brand authority — polished, animated, mobile-first */}
          <motion.div initial="hidden" animate="visible" variants={{
          hidden: {
            opacity: 0
          },
          visible: {
            opacity: 1,
            transition: {
              delayChildren: 1.1,
              staggerChildren: 0.12
            }
          }
        }} className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-acid-green/10 border border-acid-green/20 max-w-4xl" data-id="element-1092">
            {[{
            value: '50,876+',
            label: 'Units Deployed',
            icon: null
          }, {
            value: '4.9',
            label: 'Avg Rating',
            icon: <StarIcon className="w-5 h-5 text-acid-green" data-id="element-1093" />
          }, {
            value: '7 YRS',
            label: 'Brand Heritage',
            icon: <Award size={22} className="text-acid-green" strokeWidth={2} data-id="element-1094" />
          }, {
            value: 'USA',
            label: 'American Brand',
            icon: <FlagIcon className="w-7 h-5" data-id="element-1095" />
          }].map((stat, i) => <motion.div key={i} variants={{
            hidden: {
              opacity: 0,
              y: 20
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.5,
                ease: 'easeOut'
              }
            }
          }} whileHover={{
            y: -2
          }} className="group bg-base-black/80 backdrop-blur-sm p-4 sm:p-5 md:p-6 hover:bg-acid-green/5 transition-colors duration-300 cursor-default relative overflow-hidden" data-id="element-1096">
                {/* Top accent line on hover */}
                <span className="absolute top-0 left-0 h-[2px] w-0 bg-acid-green group-hover:w-full transition-all duration-500" data-id="element-1097"></span>

                <div className="flex items-center gap-2 mb-2 min-h-[36px] sm:min-h-[40px]" data-id="element-1098">
                  {stat.icon && <span className="shrink-0" data-id="element-1099">{stat.icon}</span>}
                  <span className="font-bebas text-3xl sm:text-4xl md:text-[42px] text-white leading-none tracking-tight" data-id="element-1100">
                    {stat.value.includes('+') ? <>
                        <CountUp value={stat.value.replace('+', '')} duration={2.2} data-id="element-1101" />
                        <span className="text-acid-green" data-id="element-1102">+</span>
                      </> : <CountUp value={stat.value} duration={1.8} data-id="element-1103" />}
                  </span>
                </div>
                <div className="font-barlow font-bold text-[9px] sm:text-[10px] text-text-dimmed tracking-[3px] uppercase group-hover:text-acid-green transition-colors duration-300" data-id="element-1104">
                  {stat.label}
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2" data-id="element-1105">
        <span className="font-barlow font-bold text-[10px] text-text-dimmed tracking-[4px] uppercase" data-id="element-1106">
          SCROLL
        </span>
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="w-[1px] h-10 bg-gradient-to-b from-acid-green to-transparent" data-id="element-1107"></motion.div>
      </motion.div>
    </section>;
};